import { isBoolean, isNumber, isPlainObject, isString } from 'lodash';
import { baseInputValidationScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { maskValue } from './helpers/maskValue';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';
import { TValidationScheme } from '@krugom/validator/dist/types';

const validationScheme: TValidationScheme = {
  ...baseInputValidationScheme,
  properties: {
    ...baseInputValidationScheme.properties,
    initialValue: {
      type: 'custom',
      validator: (v: unknown) => isBoolean(v) || isNumber(v) || isString(v),
    },
    value: {
      type: 'custom',
      validator: (v: unknown) => isBoolean(v) || isNumber(v) || isString(v),
    },
    mask: {
      type: 'custom',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validator: (v: any) => {
        if (isPlainObject(v)) {
          if (isString(v.exp) && (!v.flags || isString(v.flags))) return true;
          return false;
        } else if (isString(v)) {
          return true;
        } else {
          return false;
        }
      },
    },
  },
};

const allowedTypes = [
  'button', 'text', 'color', 'date', 'datetime-local', 'email',
  'number', 'password', 'search', 'tel', 'time', 'url',
  'week', 'month', 'select-one', 'select-multiple',
  'checkbox', 'radio', 'textarea',
];

const maskableTypes = new Set(['text', 'search', 'email', 'password', 'tel', 'url', 'number']);

const CustomInputSimple: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const result = validate(tagProps, validationScheme);

  const {
    name,
    type,
    children,
    required,
    mask,
    validation,
    initialValue,
    value,
    formOnly,
    label,
    description,
    placeholder,
    selector,
    autoComplete,
  } = tagProps;
  const formContextInput = name ? context.form?.inputs[name] : undefined;

  const [valueState, setValueState] = useState<TInputState['value'] | undefined>(
    value || initialValue || formContextInput?.value || ''
  );
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [focused, setFocused] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name || 'unknown-input',
      label,
      description,
      placeholder,
      type,
      value: value || initialValue,
      errors: [],
      touched: !!value,
      required,
      validation,
      initialValue,
      formOnly,
      reset: false,
      autoComplete,
    }),
    [formOnly, initialValue, name, required, validation, type, value, autoComplete, label, description, placeholder]
  );

  useEffect(() => {
    if (context.form) {
      if (!formContextInput) {
        context.form.handleSetInput(name, initialState);
      } else if (formContextInput.reset) {
        context.form.handleSetInput(name, initialState);
        setValueState(initialValue);
      }
    }
  }, [context.form, formContextInput, initialState, initialValue, name]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!context.form || value) return;
      const inputType = e.target.type;
      const inputValue = e.target.value === '' ? undefined : e.target.value;
      if (!allowedTypes.includes(inputType)) {
        console.warn(`Type ${inputType} is not supported!`);
        return;
      }
      const isMaskable = !inputType || maskableTypes.has(inputType);

      if (
        !focused &&
        isString(inputValue) &&
        isString(valueState) &&
        Math.abs(inputValue.length - valueState.length) > 1 &&
        autoComplete === 'off'
      )
        return;
      const newValue = isMaskable && mask && inputValue ? maskValue(inputValue, mask) : inputValue;
      setValueState(newValue);
      clearTimeout(timer);
      setTimer(setTimeout(() => context.form?.handleSetValue(name, newValue), 300));
    },
    [context.form, focused, mask, name, timer, value, valueState, autoComplete]
  );

  const handleFocus = useCallback(() => {
    if (!focused) setFocused(true);
  }, [focused]);

  const handleBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (focused) setFocused(false);
      if (!formContextInput || value) return;
      if (valueState !== formContextInput.value) {
        context.form?.handleSetValue(name, valueState);
      }
    },
    [context.form, formContextInput, name, value, valueState]
  );

  const handleCleanup = useCallback(() => {
    if (value) return;
    setValueState(null);
    context.form?.handleSetValue(name);
  }, [context.form, name, value]);

  const handleShowHelp = useCallback(() => {
    setShowHelp(prev => !prev);
  }, []);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      type: initialState.type,
      required,
      validation,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      value: valueState,
      label,
      description,
      placeholder,
      selector,
      showHelp,
      handleChange,
      handleFocus,
      handleBlur,
      handleCleanup,
      handleShowHelp,
      autoComplete,
    }),
    [
      name,
      initialState.type,
      required,
      validation,
      initialValue,
      formContextInput?.errors,
      formContextInput?.touched,
      valueState,
      label,
      description,
      placeholder,
      selector,
      showHelp,
      handleChange,
      handleFocus,
      handleBlur,
      handleCleanup,
      handleShowHelp,
      autoComplete,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!context.form) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return null;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputSimpleContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputSimple.displayName = 'CustomInputSimple';
export default CustomInputSimple;
